import { supabase } from "@/integrations/supabase/client"

export const PHOTO_BUCKET = "fotos-veiculos"
export const DOCUMENT_BUCKET = "documentos-veiculos"

export interface UploadablePhoto {
  url: string
  file?: File
  storagePath?: string
  originalName?: string
}

export interface UploadableDocument {
  id?: string
  tipo_documento: string
  url: string
  nome_arquivo: string
  file?: File
  storagePath?: string
}

export function isStoredFilePath(value?: string | null) {
  if (!value) return false
  return !value.startsWith("http://") && !value.startsWith("https://") && !value.startsWith("data:") && !value.startsWith("blob:")
}

function extractStoragePathFromUrl(bucket: string, rawPathOrUrl: string) {
  try {
    const parsed = new URL(rawPathOrUrl)
    const publicPrefix = `/storage/v1/object/public/${bucket}/`
    const signedPrefix = `/storage/v1/object/sign/${bucket}/`

    if (parsed.pathname.includes(publicPrefix)) {
      return decodeURIComponent(parsed.pathname.split(publicPrefix)[1] || "")
    }

    if (parsed.pathname.includes(signedPrefix)) {
      return decodeURIComponent(parsed.pathname.split(signedPrefix)[1] || "")
    }
  } catch {
    return null
  }

  return null
}

export function getUploadDisplayUrl(file: string | UploadablePhoto | UploadableDocument | null | undefined) {
  if (!file) return ""
  return typeof file === "string" ? file : file.url
}

export function revokeLocalObjectUrl(file: string | UploadablePhoto | UploadableDocument | null | undefined) {
  const url = getUploadDisplayUrl(file)
  if (url.startsWith("blob:")) {
    URL.revokeObjectURL(url)
  }
}

export function getUploadStoragePath(file: string | UploadablePhoto | UploadableDocument | null | undefined) {
  if (!file) return null
  if (typeof file === "string") return isStoredFilePath(file) ? file : null
  return file.storagePath ?? null
}

export function isSameUploadFile(
  left: string | UploadablePhoto | UploadableDocument | null | undefined,
  right: string | UploadablePhoto | UploadableDocument | null | undefined,
) {
  if (!left || !right) return false
  if (left === right) return true

  const leftPath = getUploadStoragePath(left)
  const rightPath = getUploadStoragePath(right)
  if (leftPath && rightPath) return leftPath === rightPath

  return getUploadDisplayUrl(left) === getUploadDisplayUrl(right)
}

export async function createSignedFileUrl(bucket: string, rawPathOrUrl: string | null | undefined) {
  if (!rawPathOrUrl) return null
  const extractedPath = isStoredFilePath(rawPathOrUrl)
    ? rawPathOrUrl
    : extractStoragePathFromUrl(bucket, rawPathOrUrl)

  if (!extractedPath) return rawPathOrUrl

  const { data, error } = await supabase.storage.from(bucket).createSignedUrl(extractedPath, 60 * 60)
  if (error || !data?.signedUrl) {
    console.error("Erro ao gerar signed URL:", { bucket, rawPathOrUrl, extractedPath, error })
    return null
  }

  return data.signedUrl
}

function sanitizeFileName(fileName: string) {
  return fileName
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .toLowerCase()
}

function buildStoragePath(baseFolder: string, fileName: string) {
  const extension = fileName.includes(".") ? fileName.split(".").pop() : ""
  const safeName = sanitizeFileName(fileName.replace(/\.[^.]+$/, ""))
  const unique = crypto.randomUUID()
  return `${baseFolder}/${safeName || "arquivo"}-${unique}${extension ? `.${extension}` : ""}`
}

export async function uploadFileToBucket(
  bucket: string,
  file: File,
  baseFolder: string,
): Promise<{ path: string; signedUrl: string }> {
  const path = buildStoragePath(baseFolder, file.name)
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    cacheControl: "3600",
    upsert: false,
    contentType: file.type || undefined,
  })

  if (error) throw error

  const signedUrl = await createSignedFileUrl(bucket, path)
  if (!signedUrl) {
    throw new Error("Arquivo enviado, mas não foi possível gerar URL segura")
  }

  return { path, signedUrl }
}
